import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { EMPTY_MODAL_FOOTER, EMPTY_MODAL_FOOTER_STYLE } from '@/components/residents/marketplace/shared';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useToast } from '@/contexts/admin/ToastContext';
import {
  useGetLeaseQuery,
  useGetPaymentMethodsQuery,
  useGetFeeStructureQuery,
  useSetupAutoPayMutation,
  useCancelAutoPayMutation,
  webPaymentsCompatApi,
  leasesApi,
} from '@/store/api';
import { useAppDispatch, useAppSelector } from '@/store';
import { getPaymentMethodShortLabel } from '@/utils/residents/paymentUtils';
import { strings } from '@/constants/residents/strings';
import type { PaymentMethod, FeeStructure } from '@/types/residents/payment';
import { useAmountInput } from '@/features/payments/hooks/useAmountInput';
import { selectLease, getRecurringPayments } from '@/features/payments/utils/leaseAutoPay';

import { AutopayDetailsContent, AutopayInfo } from './content/AutopayDetailsContent';
import { AutopayEditContent } from './content/AutopayEditContent';
import { ChangePaymentMethodContent } from './content/ChangePaymentMethodContent';
import { CreditCardFormContent } from './content/CreditCardFormContent';
import { ACHFormContent } from './content/ACHFormContent';
import { ManageWalletContent } from './content/ManageWalletContent';
import { ModalFooter, MonthPickerBottomSheet, MonthOption } from './content/shared';

const WarningIcon = SVG_COMPONENTS.WARNING;

// Screen types for the autopay flow (single-modal architecture)
type AutopayScreen = 'details' | 'edit' | 'changeMethod' | 'removeConfirm' | 'cardForm' | 'achForm' | 'manageWallet';

interface MobileAutopayDetailsModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** The lease ID to fetch data for */
  leaseId: number;
  /** Callback when autopay is updated or removed */
  onAutopayUpdated?: () => void;
  /** Mode: 'view' shows existing autopay details, 'setup' starts at edit form for new autopay */
  mode?: 'view' | 'setup';
}

/**
 * Mobile Autopay Details Modal
 * Fullscreen modal for viewing, editing, and removing autopay settings
 * Matches Figma designs 8, 8.a, 8.b, and 8.c
 * 
 * This component fetches its own data using the leaseId prop,
 * eliminating the need for props drilling from parent components.
 * 
 * Supports two modes:
 * - 'view' (default): Shows autopay details with edit/remove options
 * - 'setup': Starts directly at the edit form for setting up new autopay
 */
export const MobileAutopayDetailsModal: React.FC<MobileAutopayDetailsModalProps> = ({
  visible,
  onClose,
  leaseId,
  onAutopayUpdated,
  mode = 'view',
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useToast();

  // Get current user from auth state
  const authUser = useAppSelector((s) => (s as any).auth?.user);

  // Fetch lease data
  const { data: leaseData, isLoading: loadingLease, refetch: refetchLease } = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !visible || !leaseId }
  );

  // Extract lease info from response
  const leaseInfo = useMemo(() => {
    if (!leaseData) return null;

    const lease = selectLease(leaseData);
    const property = lease?.property || {};
    const users = lease?.users || [];
    const recurringPayments = getRecurringPayments(lease);
    
    // Determine if this is a connection lease
    const isConnection = !users?.some((u: any) => u?.id === authUser?.id);
    
    // Find the display user
    const connectedPrimary = users?.find((u: any) => u?.id !== authUser?.id && u?.LeaseCustomer?.customerType === 'primary');
    const displayUser = isConnection 
      ? (connectedPrimary || users?.find((u: any) => u?.id !== authUser?.id) || users?.[0] || {})
      : (users?.find((u: any) => u?.id === authUser?.id) || users?.[0] || {});
    
    // Build account name
    const accountName = isConnection 
      ? (displayUser?.firstName && displayUser?.lastName 
          ? `${displayUser.firstName} ${displayUser.lastName}` 
          : 'Resident')
      : 'My account';
    
    // Extract autopay data
    const autopay = recurringPayments[0] ? {
      id: recurringPayments[0].id,
      amount: recurringPayments[0].amount ? Number(recurringPayments[0].amount) : undefined,
      isFullAmount: !recurringPayments[0].amount || recurringPayments[0].payFullAmount,
      startDate: recurringPayments[0].startDate || recurringPayments[0].createdAt,
      endDate: recurringPayments[0].endDate,
      sourceId: recurringPayments[0].sourceId ? Number(recurringPayments[0].sourceId) : undefined,
    } : null;
    
    return {
      accountName,
      propertyName: property?.marketingName || '',
      unitNumber: lease?.unitNumberSpace || lease?.unitNumber || '',
      firstName: displayUser?.firstName,
      lastName: displayUser?.lastName,
      photoUrl: displayUser?.photoUrl,
      leaseEndDate: lease?.endDate,
      autopay,
    };
  }, [leaseData, authUser?.id]);

  // Screen navigation state - start at 'edit' for setup mode, 'details' for view mode
  const [screenHistory, setScreenHistory] = useState<AutopayScreen[]>([mode === 'setup' ? 'edit' : 'details']);
  const currentScreen = screenHistory[screenHistory.length - 1];

  // Edit form state
  const {
    amountType,
    customAmount,
    inputRef: amountInputRef,
    selectBalance,
    selectOther,
    handleAmountChange,
    setAmountType,
    setCustomAmount,
  } = useAmountInput();
  const [dateRangeType, setDateRangeType] = useState<'leaseEnd' | 'custom'>('leaseEnd');
  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);

  // Custom date range state
  const [firstPaymentMonth, setFirstPaymentMonth] = useState<string>('');
  const [lastPaymentMonth, setLastPaymentMonth] = useState<string>('');
  const [showFirstPaymentPicker, setShowFirstPaymentPicker] = useState(false);
  const [showLastPaymentPicker, setShowLastPaymentPicker] = useState(false);

  // API queries
  const { data: methodsData, isLoading: loadingMethods, refetch: refetchMethods } = useGetPaymentMethodsQuery(undefined, {
    skip: !visible,
  });
  const { data: feeData } = useGetFeeStructureQuery(undefined, {
    skip: !visible,
  });
  const [setupAutopay, { isLoading: savingAutopay }] = useSetupAutoPayMutation();
  const [cancelAutopay, { isLoading: cancellingAutopay }] = useCancelAutoPayMutation();

  const isLoading = loadingLease || loadingMethods || savingAutopay || cancellingAutopay;

  // Payment methods
  const paymentMethods: PaymentMethod[] = useMemo(() => {
    const methods = methodsData?.response || [];
    return methods.map((m: any) => ({
      id: m.id,
      sourceId: m.sourceId || m.id,
      type: m.type === 'card' ? 'card' : 'ach',
      name: m.name,
      last4: m.last4,
      brand: m.brand || m.accttype,
      bankName: m.bankName || m.name,
      isExpired: m.isExpired,
      isDefault: m.isDefault,
    }));
  }, [methodsData]);

  // Selected payment method
  const selectedPaymentMethod = useMemo(() => {
    if (!selectedMethodId) return null;
    return paymentMethods.find((m) => {
      const methodSourceId = Number(m.sourceId) || m.id;
      return methodSourceId === selectedMethodId;
    }) || null;
  }, [selectedMethodId, paymentMethods]);

  // Fee structure
  const feeStructure: FeeStructure = useMemo(() => {
    const fees = feeData?.response || {};
    return {
      creditCard: fees.creditCard || '3.39%',
      debitCard: fees.debitCard || '2%',
      bankAccount: fees.bankAccount || '$3.99',
    };
  }, [feeData]);

  // Generate month options for date pickers (from current month to lease end or 12 months ahead)
  const monthOptions: MonthOption[] = useMemo(() => {
    const options: MonthOption[] = [];
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    // Determine end date - use lease end date or 12 months from now
    let endDate: Date;
    if (leaseInfo?.leaseEndDate) {
      endDate = new Date(leaseInfo.leaseEndDate);
    } else {
      endDate = new Date(now.getFullYear() + 1, now.getMonth(), 1);
    }

    // Generate month options
    const iterDate = new Date(currentMonth);
    while (iterDate <= endDate) {
      const monthLabel = iterDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const monthValue = `${iterDate.getFullYear()}-${String(iterDate.getMonth() + 1).padStart(2, '0')}`;
      options.push({ label: monthLabel, value: monthValue });
      iterDate.setMonth(iterDate.getMonth() + 1);
    }

    return options;
  }, [leaseInfo?.leaseEndDate]);

  // Check if user has valid payment method
  const hasPaymentMethod = useMemo(() => {
    return selectedMethodId !== null && paymentMethods.some((m) => {
      const methodSourceId = Number(m.sourceId) || m.id;
      return methodSourceId === selectedMethodId && !m.isExpired;
    });
  }, [selectedMethodId, paymentMethods]);

  // Get payment method label
  const paymentMethodLabel = useMemo(() => {
    if (selectedPaymentMethod) {
      return getPaymentMethodShortLabel(selectedPaymentMethod);
    }
    // Try to find from original sourceId
    const autopaySourceId = leaseInfo?.autopay?.sourceId;
    if (autopaySourceId) {
      const method = paymentMethods.find((m) => {
        const methodSourceId = Number(m.sourceId) || m.id;
        return methodSourceId === autopaySourceId;
      });
      if (method) return getPaymentMethodShortLabel(method);
    }
    return '—';
  }, [selectedPaymentMethod, leaseInfo?.autopay?.sourceId, paymentMethods]);

  // Autopay info for display
  const autopayInfo: AutopayInfo | null = useMemo(() => {
    if (!leaseInfo?.autopay) return null;
    return {
      id: leaseInfo.autopay.id,
      amount: leaseInfo.autopay.amount,
      isFullAmount: leaseInfo.autopay.isFullAmount,
      startDate: leaseInfo.autopay.startDate,
      endDate: leaseInfo.autopay.endDate,
      sourceId: leaseInfo.autopay.sourceId,
      paymentMethodLabel,
    };
  }, [leaseInfo?.autopay, paymentMethodLabel]);

  // Initialize form state when modal opens or lease data changes
  useEffect(() => {
    if (visible) {
      if (leaseInfo?.autopay) {
        // Existing autopay - initialize with existing values
        setAmountType(leaseInfo.autopay.isFullAmount ? 'balance' : 'other');
        setCustomAmount(leaseInfo.autopay.amount ? leaseInfo.autopay.amount.toString() : '');
        setDateRangeType('leaseEnd');
        setSelectedMethodId(leaseInfo.autopay.sourceId || null);
        setFirstPaymentMonth('');
        setLastPaymentMonth('');
        setScreenHistory([mode === 'setup' ? 'edit' : 'details']);
      } else if (mode === 'setup') {
        // Setup mode with no existing autopay - initialize with defaults
        setAmountType('balance');
        setCustomAmount('');
        setDateRangeType('leaseEnd');
        setFirstPaymentMonth('');
        setLastPaymentMonth('');
        // Set default payment method
        const defaultMethod = paymentMethods.find((m) => m.isDefault && !m.isExpired);
        const firstValidMethod = paymentMethods.find((m) => !m.isExpired);
        const method = defaultMethod || firstValidMethod;
        if (method) {
          const sourceId = Number(method.sourceId) || (typeof method.id === 'string' ? parseInt(method.id, 10) : method.id);
          setSelectedMethodId(sourceId);
        } else {
          setSelectedMethodId(null);
        }
        setScreenHistory(['edit']);
      }
    }
  }, [visible, leaseInfo?.autopay, mode, paymentMethods, setAmountType, setCustomAmount]);

  // Navigation helpers
  const navigateTo = useCallback((screen: AutopayScreen) => {
    setScreenHistory((prev) => [...prev, screen]);
  }, []);

  const goBack = useCallback(() => {
    if (screenHistory.length > 1) {
      setScreenHistory((prev) => prev.slice(0, -1));
    }
  }, [screenHistory]);

  const resetToDetails = useCallback(() => {
    setScreenHistory(['details']);
  }, []);

  // Handlers
  const handleEditSettings = useCallback(() => {
    navigateTo('edit');
  }, [navigateTo]);

  const handleRemove = useCallback(() => {
    navigateTo('removeConfirm');
  }, [navigateTo]);

  const handleRemoveCancel = useCallback(() => {
    goBack();
  }, [goBack]);

  const handleRemoveProceed = useCallback(async () => {
    if (!leaseInfo?.autopay?.id) return;
    
    try {
      await cancelAutopay(leaseInfo.autopay.id).unwrap();
      // Invalidate caches
      dispatch(webPaymentsCompatApi.util.invalidateTags(['autoPayStatus']));
      dispatch(leasesApi.util.resetApiState());
      onAutopayUpdated?.();
      onClose();
      // Show toast after modal closes so it renders above the app root
      setTimeout(() => {
        showSuccess(strings.mobilePayments.autopayRemovedSuccess || 'Autopay removed successfully');
      }, 300);
    } catch (error: any) {
      const message = error?.data?.message || 'Failed to remove autopay';
      showError(message);
    }
  }, [leaseInfo?.autopay?.id, cancelAutopay, dispatch, showSuccess, showError, onAutopayUpdated, onClose]);

  const handleChangePaymentMethod = useCallback(() => {
    navigateTo('changeMethod');
  }, [navigateTo]);

  const handleMethodSelected = useCallback((methodId: number) => {
    setSelectedMethodId(methodId);
    goBack();
  }, [goBack]);

  // Add card handler - navigates to card form screen
  const handleAddCard = useCallback(() => {
    navigateTo('cardForm');
  }, [navigateTo]);

  // Add ACH handler - navigates to ACH form screen
  const handleAddACH = useCallback(() => {
    navigateTo('achForm');
  }, [navigateTo]);

  // Manage wallet handler - navigates to manage wallet screen
  const handleManageWallet = useCallback(() => {
    navigateTo('manageWallet');
  }, [navigateTo]);

  // Handle payment method added from card/ACH form
  const handlePaymentMethodAdded = useCallback((methodId?: number) => {
    if (methodId) {
      setSelectedMethodId(methodId);
    }
    // Refetch methods and go back to change method screen
    refetchMethods();
    goBack();
  }, [goBack, refetchMethods]);

  // Handle wallet methods updated (set default, delete, etc.)
  const handleWalletMethodsUpdated = useCallback(() => {
    // Refetch payment methods to reflect changes
    refetchMethods();
    
    // If the currently selected method was deleted, clear selection
    if (selectedMethodId) {
      const methodStillExists = paymentMethods.some((m) => {
        const sourceId = Number(m.sourceId) || m.id;
        return sourceId === selectedMethodId;
      });
      if (!methodStillExists) {
        // Find new default or first available method
        const defaultMethod = paymentMethods.find((m) => m.isDefault && !m.isExpired);
        const firstValidMethod = paymentMethods.find((m) => !m.isExpired);
        const newMethod = defaultMethod || firstValidMethod;
        if (newMethod) {
          const newId = Number(newMethod.sourceId) || (typeof newMethod.id === 'string' ? parseInt(newMethod.id, 10) : newMethod.id);
          setSelectedMethodId(newId);
        } else {
          setSelectedMethodId(null);
        }
      }
    }
    // Go back after updating
    goBack();
  }, [refetchMethods, selectedMethodId, paymentMethods, goBack]);

  const handleEditCancel = useCallback(() => {
    // In setup mode, just close the modal
    if (mode === 'setup') {
      onClose();
      return;
    }
    // In view mode, reset form state to original values and go back
    if (leaseInfo?.autopay) {
      setAmountType(leaseInfo.autopay.isFullAmount ? 'balance' : 'other');
      setCustomAmount(leaseInfo.autopay.amount ? leaseInfo.autopay.amount.toString() : '');
      setDateRangeType('leaseEnd');
      setSelectedMethodId(leaseInfo.autopay.sourceId || null);
      setFirstPaymentMonth('');
      setLastPaymentMonth('');
    }
    goBack();
  }, [leaseInfo?.autopay, goBack, mode, onClose, setAmountType, setCustomAmount]);

  const handleEditConfirm = useCallback(async () => {
    if (!selectedMethodId) {
      showError('Please select a payment method');
      return;
    }

    if (amountType === 'other') {
      const amount = parseFloat(customAmount);
      if (!amount || amount <= 0) {
        showError('Please enter a valid amount');
        return;
      }
    }

    // Validate custom date range
    if (dateRangeType === 'custom') {
      if (!firstPaymentMonth || !lastPaymentMonth) {
        showError('Please select both first and last payment months');
        return;
      }
    }

    try {
      const autopayPayload: any = {
        leaseId,
        sourceId: selectedMethodId,
        autopayIsLeaseLifetime: dateRangeType === 'leaseEnd',
        autopayIsFullAmount: amountType === 'balance',
      };

      if (amountType === 'other') {
        autopayPayload.fixedAmount = parseFloat(customAmount);
      }

      // Add custom date range if selected
      if (dateRangeType === 'custom' && firstPaymentMonth && lastPaymentMonth) {
        // Convert "2025-01" format to ISO date string "2025-01-01T00:00:00.000Z"
        // First payment starts on the 1st of the selected month
        autopayPayload.startDate = `${firstPaymentMonth}-01T00:00:00.000Z`;
        // Last payment ends on the last day of the selected month
        const [year, month] = lastPaymentMonth.split('-').map(Number);
        const lastDayOfMonth = new Date(year, month, 0).getDate();
        autopayPayload.endDate = `${lastPaymentMonth}-${String(lastDayOfMonth).padStart(2, '0')}T23:59:59.999Z`;
      }

      await setupAutopay(autopayPayload).unwrap();

      // Invalidate caches
      dispatch(webPaymentsCompatApi.util.invalidateTags(['autoPayStatus']));
      dispatch(leasesApi.util.resetApiState());

      // Different behavior for setup vs edit mode
      if (mode === 'setup') {
        onAutopayUpdated?.();
        onClose();
        // Show toast after modal closes so it renders above the app root
        setTimeout(() => {
          showSuccess('Autopay has been set up successfully!');
        }, 300);
      } else {
        onAutopayUpdated?.();
        refetchLease();
        resetToDetails();
        // Show toast after modal state settles
        setTimeout(() => {
          showSuccess('Autopay settings updated successfully');
        }, 300);
      }
    } catch (error: any) {
      const fallback = mode === 'setup' ? 'Failed to setup autopay' : 'Failed to update autopay';
      console.error('Failed to setup autopay:', error);
      showError(error?.data?.error?.message || fallback);
    }
  }, [
    selectedMethodId,
    amountType,
    customAmount,
    leaseId,
    dateRangeType,
    firstPaymentMonth,
    lastPaymentMonth,
    setupAutopay,
    dispatch,
    showSuccess,
    showError,
    onAutopayUpdated,
    refetchLease,
    resetToDetails,
    mode,
    onClose,
  ]);

  // Get title based on current screen
  const getTitle = (): string => {
    const accountName = leaseInfo?.accountName || 'Account';
    switch (currentScreen) {
      case 'details':
        return strings.mobilePayments.autopayFor(accountName);
      case 'edit':
        // Different title for setup vs edit mode
        return mode === 'setup' 
          ? `Setup autopay for ${accountName}`
          : strings.mobilePayments.autopayFor(accountName);
      case 'changeMethod':
        return mode === 'setup' ? 'Select payment method' : 'Change payment method';
      case 'cardForm':
        return 'Credit or debit card details';
      case 'achForm':
        return 'ACH details';
      case 'manageWallet':
        return 'Manage my wallet';
      case 'removeConfirm':
        return ''; // No title for confirmation screen
      default:
        return '';
    }
  };

  const getSubtitle = (): string | undefined => {
    // No subtitle for these screens
    if (['changeMethod', 'removeConfirm', 'cardForm', 'achForm', 'manageWallet'].includes(currentScreen)) {
      return undefined;
    }
    const { propertyName, unitNumber } = leaseInfo || {};
    return `${propertyName || ''}${unitNumber ? `, Unit ${unitNumber}` : ''}`;
  };

  // Loading state when lease data not yet available
  if (visible && (loadingLease || !leaseInfo)) {
    return (
      <UnifiedModal
        visible={visible}
        onClose={onClose}
        variant="fullscreen"
        showHeader={false}
        contentContainerStyle={centeredContentStyle}
      >
        <View style={styles.loadingContent}>
          <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentBlueSolid} />
          <Text style={[styles.loadingText, { color: MARKETPLACE_COLORS.textSecondary }]}>Loading autopay details...</Text>
        </View>
      </UnifiedModal>
    );
  }

  // Don't render if no autopay data (unless in setup mode)
  if (!autopayInfo && mode !== 'setup') {
    return null;
  }

  // Render content based on current screen
  const renderContent = () => {
    switch (currentScreen) {
      case 'details':
        // Details screen only accessible in view mode when autopayInfo exists
        if (!autopayInfo) return null;
        return (
          <AutopayDetailsContent
            accountName={leaseInfo?.accountName || 'Account'}
            firstName={leaseInfo?.firstName}
            lastName={leaseInfo?.lastName}
            photoUrl={leaseInfo?.photoUrl}
            autopay={autopayInfo}
            thirdPartyAutopays={[]}
            onEditSettings={handleEditSettings}
            onRemove={handleRemove}
          />
        );

      case 'edit':
        // Format month values for display (e.g., "2024-08" -> "Aug, 2024")
        const formatMonthDisplay = (monthValue: string): string => {
          if (!monthValue) return '';
          const [year, month] = monthValue.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1, 1);
          return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        };

        return (
          <>
            <AutopayEditContent
              autopay={{
                amount: leaseInfo?.autopay?.amount,
                isFullAmount: leaseInfo?.autopay?.isFullAmount,
                startDate: leaseInfo?.autopay?.startDate,
                endDate: leaseInfo?.autopay?.endDate,
                paymentMethodLabel: hasPaymentMethod ? paymentMethodLabel : undefined,
              }}
              leaseEndDate={leaseInfo?.leaseEndDate}
              feeStructure={feeStructure}
              onChangePaymentMethod={handleChangePaymentMethod}
              onAddPaymentMethod={handleChangePaymentMethod}
              hasPaymentMethod={hasPaymentMethod}
              amountType={amountType}
              customAmount={customAmount}
              dateRangeType={dateRangeType}
              onAmountTypeChange={(type) => type === 'other' ? selectOther() : selectBalance()}
              onCustomAmountChange={handleAmountChange}
              onDateRangeTypeChange={setDateRangeType}
              inputRef={amountInputRef}
              firstPaymentMonth={formatMonthDisplay(firstPaymentMonth)}
              lastPaymentMonth={formatMonthDisplay(lastPaymentMonth)}
              onFirstPaymentPress={() => setShowFirstPaymentPicker(true)}
              onLastPaymentPress={() => setShowLastPaymentPicker(true)}
            />

            {/* First Payment Month Picker */}
            <MonthPickerBottomSheet
              visible={showFirstPaymentPicker}
              onClose={() => setShowFirstPaymentPicker(false)}
              title="First payment"
              options={monthOptions}
              selectedValue={firstPaymentMonth}
              onSelect={setFirstPaymentMonth}
            />

            {/* Last Payment Month Picker */}
            <MonthPickerBottomSheet
              visible={showLastPaymentPicker}
              onClose={() => setShowLastPaymentPicker(false)}
              title="Last payment"
              options={monthOptions}
              selectedValue={lastPaymentMonth}
              onSelect={setLastPaymentMonth}
            />
          </>
        );

      case 'changeMethod':
        return (
          <ChangePaymentMethodContent
            paymentMethods={paymentMethods}
            selectedMethodId={selectedMethodId}
            onMethodSelected={handleMethodSelected}
            onAddCard={handleAddCard}
            onAddACH={handleAddACH}
            onManageWallet={handleManageWallet}
            feeStructure={feeStructure}
          />
        );

      case 'cardForm':
        return (
          <CreditCardFormContent
            onCardAdded={handlePaymentMethodAdded}
            onBack={goBack}
          />
        );

      case 'achForm':
        return (
          <ACHFormContent
            onACHAdded={handlePaymentMethodAdded}
            onBack={goBack}
          />
        );

      case 'manageWallet':
        return (
          <ManageWalletContent
            paymentMethods={paymentMethods}
            onMethodsUpdated={handleWalletMethodsUpdated}
          />
        );

      case 'removeConfirm':
        return (
          <View style={styles.removeConfirmContent}>
            <View style={styles.removeConfirmMessageBox}>
              {WarningIcon && (
                <View style={styles.removeConfirmIconContainer}>
                  <WarningIcon width={48} height={48} color={MARKETPLACE_COLORS.textSecondary} />
                </View>
              )}
              <Text style={[styles.removeConfirmMessage, { color: MARKETPLACE_COLORS.textPrimary }]}>
                {strings.mobilePayments.removeAutopayConfirm}
              </Text>
            </View>
          </View>
        );

      default:
        return null;
    }
  };

  // Render footer based on current screen
  const renderFooter = () => {
    if (isLoading) {
      return (
        <View style={styles.footerLoading}>
          <ActivityIndicator size="small" color={MARKETPLACE_COLORS.accentBlueSolid} />
        </View>
      );
    }

    switch (currentScreen) {
      case 'details':
        return (
          <ModalFooter
            variant="single"
            primaryButton={{ label: 'Done', onPress: onClose }}
          />
        );

      case 'edit':
        return (
          <ModalFooter
            variant="double"
            secondaryButton={{ label: 'Cancel', onPress: handleEditCancel }}
            primaryButton={{ label: 'Confirm', onPress: handleEditConfirm }}
          />
        );

      case 'changeMethod':
        return (
          <ModalFooter
            variant="double"
            secondaryButton={{ label: 'Cancel', onPress: goBack }}
            primaryButton={{ label: 'Save', onPress: goBack }}
          />
        );

      case 'cardForm':
      case 'achForm':
        // Footer is handled inside the content component for form submission
        return null;

      case 'manageWallet':
        return (
          <ModalFooter
            variant="single"
            primaryButton={{ label: 'Done', onPress: goBack }}
          />
        );

      case 'removeConfirm':
        return (
          <ModalFooter
            variant="double"
            secondaryButton={{ label: 'No, cancel', onPress: handleRemoveCancel }}
            primaryButton={{ label: 'Proceed', onPress: handleRemoveProceed }}
          />
        );

      default:
        return null;
    }
  };

  const title = getTitle();
  const subtitle = getSubtitle();
  const footerContent = renderFooter();
  const hasEmbeddedFooter = currentScreen === 'cardForm' || currentScreen === 'achForm';

  return (
    <UnifiedModal
      visible={visible}
      onClose={onClose}
      variant="fullscreen"
      title={title}
      subtitle={subtitle}
      showHeader={currentScreen !== 'removeConfirm'}
      headerStyle={headerStyleOverride}
      footer={hasEmbeddedFooter ? EMPTY_MODAL_FOOTER : footerContent}
      footerStyle={hasEmbeddedFooter ? EMPTY_MODAL_FOOTER_STYLE : undefined}
      avoidKeyboard
      scrollable
      contentContainerStyle={
        currentScreen === 'removeConfirm' ? centeredScrollContentOverride : scrollContentOverride
      }
    >
      {renderContent()}
    </UnifiedModal>
  );
};

const headerStyleOverride = { alignItems: 'flex-start' as const, gap: 6 };
const scrollContentOverride = { paddingHorizontal: 0, paddingBottom: 0, flexGrow: 1 };
const centeredContentStyle = { flex: 1, justifyContent: 'center' as const, alignItems: 'center' as const };
const centeredScrollContentOverride = { ...scrollContentOverride, justifyContent: 'center' as const, alignItems: 'center' as const };

const styles = StyleSheet.create({
  loadingContent: {
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 14,
    fontWeight: '400',
  },
  footerLoading: {
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeConfirmContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 24,
  },
  removeConfirmMessageBox: {
    alignItems: 'center',
    gap: 16,
  },
  removeConfirmIconContainer: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeConfirmMessage: {
    fontSize: 16,
    fontWeight: '400',
    textAlign: 'center',
    lineHeight: 24,
  },
});

export default MobileAutopayDetailsModal;
